import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { downloadTranscript } from '../../utils/transcripts';
import { EMPTY_DOC, exportTextFromDoc, textFromDoc, type TranscriptDoc } from '../../utils/transcriptDoc';
import { fetchDeliverableTranscript, type StoredTranscript } from '../../utils/transcriptStore';
import { fetchTranscriptComments, insertTranscriptComment, type TranscriptComment } from '../../utils/transcriptComments';
import { TranscriptVersions } from '../../components/TranscriptVersions';
import { TranscriptEditor } from '../../components/TranscriptEditor';
import { GeneralComments } from '../../components/TranscriptComments';
type LoadState = 'loading' | 'ready' | 'error';

/**
 * A transcript, open for light corrections.
 *
 * The document — including every tracked deletion — is read straight from
 * Supabase for this project and deliverable, and so are the comments. Nothing
 * here depends on what this browser happens to hold, so the same transcript,
 * the same strikeouts and the same notes appear on every device with the
 * project link.
 */
export function Transcript() {
  const {
    activeProject,
    activeDeliverable,
    goClient,
    saveTranscript,
    canEditTranscripts,
    canComment,
    clientName,
    notify
  } = usePortal();
  const [load, setLoad] = useState<LoadState>('loading');
  const [stored, setStored] = useState<StoredTranscript | null>(null);
  /** The working document, including its tracked deletions. */
  const [doc, setDoc] = useState<TranscriptDoc>(EMPTY_DOC);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [comments, setComments] = useState<TranscriptComment[]>([]);
  const deliverableKey = activeDeliverable?.id ?? '';
  const projectSlug = activeProject.slug;
  const readComments = useCallback(async () => {
    if (!projectSlug || !deliverableKey) return;
    const result = await fetchTranscriptComments(projectSlug, deliverableKey, clientName);
    if (result.status === 'ok') setComments(result.comments);
  }, [projectSlug, deliverableKey, clientName]);

  /* Read the authoritative transcript whenever the open deliverable changes. */
  useEffect(() => {
    if (!deliverableKey || !projectSlug) return;
    let live = true;
    setLoad('loading');
    setFailed('');
    void fetchDeliverableTranscript(projectSlug, deliverableKey).then(result => {
      if (!live) return;
      if (result.status === 'error') {
        /* A failed read is never presented as an empty transcript. */
        setLoad('error');
        setFailed(result.message);
        return;
      }
      setStored(result.transcript);
      setDoc(result.transcript?.doc ?? EMPTY_DOC);
      setSelectedId(null);
      setResetKey(key => key + 1);
      setLoad('ready');
    });
    void readComments();
    return () => {
      live = false;
    };
  }, [projectSlug, deliverableKey, readComments]);
  const saved = stored?.text ?? '';
  const savedDoc = stored?.doc ?? EMPTY_DOC;
  const revisions = stored?.revisions ?? [];
  const original = revisions[0];
  const latest = revisions[revisions.length - 1];
  const selected = revisions.find(entry => entry.id === selectedId) ?? latest;
  const isOriginal = Boolean(selected) && selected?.id === original?.id;
  const viewingLatest = !selected || selected.id === latest?.id;

  /** The transcript as it now reads, used to place comments and to save. */
  const text = useMemo(() => textFromDoc(doc), [doc]);

  /* A comment sits beside its passage while that passage is still there; one
     whose passage has gone is kept, and moves to the general list. */
  const anchored = useMemo(() => comments.filter(comment => comment.quote && text.includes(comment.quote)), [comments, text]);
  const general = useMemo(() => comments.filter(comment => !anchored.includes(comment)), [comments, anchored]);
  if (!activeDeliverable) {
    return <p className="label m-0 text-mute" data-id="element-703" data-mp="33p4ueb43yt5p">This transcript is no longer available.</p>;
  }
  const item = activeDeliverable;
  const dirty = text !== saved || JSON.stringify(doc) !== JSON.stringify(savedDoc);

  /**
   * Corrections belong to a client once the transcript is genuinely with them
   * for review. An internal transcript, or one still in production, is
   * read-only.
   */
  const openForReview = item.status === 'Ready for Review' || item.status === 'Changes Requested';
  const editable = canEditTranscripts && item.visibleToClient && openForReview && Boolean(saved);
  async function addComment(input: {
    quote: string;
    quoteStart: number;
    body: string;
  }) {
    const result = await insertTranscriptComment({
      projectSlug,
      deliverableKey,
      displayName: clientName || 'Client',
      body: input.body,
      quote: input.quote,
      quoteStart: input.quoteStart,
      revisionId: latest?.id ?? ''
    });
    if (!result.ok) return false;
    await readComments();
    notify('Comment added');
    return true;
  }
  async function save() {
    if (!dirty || saving) return;
    setSaving(true);
    setFailed('');
    const ok = await saveTranscript(item.id, text, doc);
    setSaving(false);
    if (!ok) {
      /* The edit stays exactly where it is, unsaved and visible. */
      setFailed('Not saved. Your changes are still here — try again.');
      return;
    }

    /* Re-read, so what is on screen is what the database now holds. */
    const result = await fetchDeliverableTranscript(projectSlug, deliverableKey);
    if (result.status === 'ok' && result.transcript) {
      setStored(result.transcript);
      setDoc(result.transcript.doc);
    }
    setSelectedId(null);
    setResetKey(key => key + 1);
    notify('Transcript saved');
  }

  /*
   * The save control, as a bar fixed to the bottom of the viewport whenever
   * there is something to save. A transcript can run far longer than one
   * screen, and a Save button that only lived at the end of it was easy to
   * lose track of — an edit could sit unsaved off-screen with nothing on
   * screen to say so. Rendered through a portal straight onto <body>, so it
   * truly stays put against the viewport rather than the page's own scroll
   * transform.
   */
  const saveBar = editable && viewingLatest ? createPortal(<div className={`fixed inset-x-0 bottom-0 z-40 border-t bg-paper/95 backdrop-blur transition-colors duration-150 ease-out ${dirty ? 'border-ink' : 'border-rule'}`} data-id="element-704" data-mp="1vdibw728r53i">
            <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-6 px-6 py-5 sm:px-9" data-id="element-705" data-mp="1lh58uikt3ew3">
              <button type="button" onClick={save} disabled={!dirty || saving} className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper disabled:border-rule disabled:text-mute disabled:hover:bg-transparent disabled:hover:text-mute" data-id="element-706" data-mp="3ks6ejgl158oc">
                {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
              </button>
              {failed ? <p role="alert" className="label-sm m-0 text-ink" data-id="element-707" data-mp="5vbelmec478j">
                  {failed}
                </p> : dirty ? <p className="label-sm m-0 text-charcoal" data-id="element-708" data-mp="34gmr0uzc7l59">Unsaved changes</p> : <p className="label-sm m-0 text-mute" data-id="element-709" data-mp="39f6c6t63wswk">
                  Select any wording for Comment and Delete · click struck wording to restore
                </p>}
            </div>
          </div>, document.body) : null;
  return <div className={editable ? 'pb-32' : undefined} data-id="element-710" data-mp="tlp7ff4qua46">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-711" data-mp="yl1y6ayqk1c1">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-712" data-mp="1er133xkujla8" />
        All Deliverables
      </button>

      <h1 className="mt-10 max-w-3xl font-display text-4xl leading-[1.02] tracking-tight sm:text-6xl" data-id="element-713" data-mp="1oo5vif5acczh">
        {item.name}
      </h1>

      {load === 'loading' ? <p className="mt-12 max-w-4xl font-display text-xl text-mute" data-id="element-714" data-mp="1ye8ucvz38l5q">Loading transcript…</p> : load === 'error' ? <div className="mt-12 max-w-4xl border border-rule bg-paperDeep px-6 py-8 sm:px-9" data-id="element-715" data-mp="2ce9w0dn6fwm9">
          <p className="m-0 font-display text-xl text-charcoal" data-id="element-716" data-mp="27ef6oej1vf3b">
            The transcript could not be loaded.
          </p>
          <p className="label-sm mt-4 text-mute" data-id="element-717" data-mp="i0s46efqodlf">Please try again, or contact Portal.</p>
        </div> : !saved ? <div className="mt-12 max-w-4xl border border-rule bg-paperDeep px-6 py-8 sm:px-9" data-id="element-718" data-mp="d2ql3tibce9y">
          <p className="m-0 font-display text-xl text-charcoal" data-id="element-719" data-mp="2op16cuxphvwa">
            The transcript has not been added yet.
          </p>
          <p className="label-sm mt-4 text-mute" data-id="element-720" data-mp="2eyfzkk7vpl2a">
            We will let you know as soon as it is ready to read.
          </p>
        </div> : <>
          {/* Downloading is the most-used action, so it sits with the title. */}
          <div className="mt-8" data-id="element-721" data-mp="25wymlaprpzht">
            <button type="button" onClick={() => downloadTranscript(item.name, exportTextFromDoc(savedDoc))} className="label flex items-center gap-3 border border-rule px-7 py-4 text-graphite transition-colors duration-150 ease-out hover:border-ink hover:text-ink" data-id="element-722" data-mp="25zulb19fws7b">
              Download
              <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-723" data-mp="3qdd3f08q2etp" />
            </button>
          </div>

          <GeneralComments comments={general} canComment={canComment} onAdd={body => addComment({
        quote: '',
        quoteStart: -1,
        body
      })} data-id="element-724" data-mp="3vbmzdd29tjgd" />

          <div className="mt-10 max-w-4xl" data-id="element-725" data-mp="kl6rpb9v9ape">
            <TranscriptVersions revisions={revisions} selectedId={selected?.id ?? ''} onSelect={id => {
          setSelectedId(id);
          // Returning to the latest edit reopens the live working document.
          if (id === latest?.id) setResetKey(key => key + 1);
        }} data-id="element-726" data-mp="km9i7i3a1b4s" />
          </div>

          <div className="mt-12 max-w-4xl" data-id="element-727" data-mp="13ihn2ksdnjzk">
            {viewingLatest ? <>
                <span className="label block text-graphite" data-id="element-728" data-mp="olqoueufwebj">Transcript</span>
                <div className="mt-5" data-id="element-729" data-mp="tktt3x2ligig">
                  <TranscriptEditor doc={doc} editable={editable} resetKey={resetKey} onChange={setDoc} comments={anchored} canComment={canComment} onComment={addComment} data-id="element-730" data-mp="tjs8b0vs2fnj" />
                </div>

                {!editable && <p className="label-sm mt-9 m-0 text-mute" data-id="element-731" data-mp="1remffik0prkv">This transcript is not open for changes</p>}
              </> : isOriginal ? (/* The original upload, exactly as Portal delivered it. */
        <div className="whitespace-pre-wrap border border-rule bg-paperDeep px-6 py-6 font-display text-xl leading-relaxed text-charcoal sm:px-9 sm:py-8 sm:text-2xl" data-id="element-732" data-mp="1dnr47zm1qye4">
                {selected?.text}
              </div>) : (/* An earlier saved state, with the deletions it carried. */
        <TranscriptEditor doc={selected?.doc ?? EMPTY_DOC} editable={false} resetKey={resetKey + (selected ? revisions.indexOf(selected) + 1 : 0)} onChange={() => undefined} comments={[]} canComment={false} onComment={async () => false} data-id="element-733" data-mp="1hot3ggfyrag3" />)}
          </div>
        </>}

      {saveBar}
    </div>;
}